import { useForm } from "@mantine/form";
import {
  PasswordInput,
  Text,
  Paper,
  Button,
  Divider,
  Stack,
  Anchor,
  Alert,
} from "@mantine/core";
import { IconCheck, IconAlertCircle } from "@tabler/icons-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useState } from "react";
import { url } from "../misc/api";

export function ResetPasswordScreen() {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const form = useForm({
    initialValues: { password: "", confirmPassword: "" },
    validate: {
      password: (val) =>
        val.length >= 6 ? null : "Password must be at least 6 characters",
      confirmPassword: (val, values) =>
        val === values.password ? null : "Passwords do not match",
    },
  });

  const handleSubmit = async () => {
    if (!token) {
      setError("Invalid or missing reset token. Please request a new link.");
      return;
    }

    setError(null);
    setLoading(true);
    try {
      const response = await fetch(url + "auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password: form.values.password }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || "Reset failed. Token may have expired.");
      }

      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div
        style={{
          backgroundColor: "darkslategray",
          paddingTop: "10%",
          display: "flex",
          paddingBottom: 500,
        }}
      >
        <Paper
          radius="md"
          p="xl"
          withBorder
          style={{ width: 500, marginLeft: "auto", marginRight: "auto" }}
        >
          <Alert icon={<IconAlertCircle size="1rem" />} color="red">
            Invalid or missing reset token. Please request a new password reset.
          </Alert>
          <Anchor
            component="button"
            type="button"
            color="dimmed"
            onClick={() => navigate("/forgot-password")}
            size="xs"
            style={{ marginTop: 20 }}
          >
            Request new reset link
          </Anchor>
        </Paper>
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: "darkslategray",
        paddingTop: "10%",
        display: "flex",
        paddingBottom: 500,
      }}
    >
      <Paper
        radius="md"
        p="xl"
        withBorder
        style={{ width: 500, marginLeft: "auto", marginRight: "auto" }}
      >
        <Text size="lg" fw={500}>
          Set New Password
        </Text>
        <Divider label="Enter your new password" labelPosition="center" my="lg" />

        {success ? (
          <>
            <Alert icon={<IconCheck size="1rem" />} color="green" mb="md">
              Your password has been reset successfully!
            </Alert>
            <Button radius="xl" fullWidth onClick={() => navigate("/login")}>
              Go to Login
            </Button>
          </>
        ) : (
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack>
              <PasswordInput
                required
                label="New Password"
                placeholder="At least 6 characters"
                value={form.values.password}
                onChange={(event) =>
                  form.setFieldValue("password", event.currentTarget.value)
                }
                error={form.errors.password}
                radius="md"
              />
              <PasswordInput
                required
                label="Confirm Password"
                placeholder="Repeat password"
                value={form.values.confirmPassword}
                onChange={(event) =>
                  form.setFieldValue(
                    "confirmPassword",
                    event.currentTarget.value
                  )
                }
                error={form.errors.confirmPassword}
                radius="md"
              />

              {error && (
                <Alert icon={<IconAlertCircle size="1rem" />} color="red">
                  {error}
                </Alert>
              )}

              <Button type="submit" radius="xl" loading={loading}>
                Reset Password
              </Button>
            </Stack>
          </form>
        )}

        <Anchor
          component="button"
          type="button"
          color="dimmed"
          onClick={() => navigate("/login")}
          size="xs"
          style={{ marginTop: 20 }}
        >
          Back to Login
        </Anchor>
      </Paper>
    </div>
  );
}
